import React, { useState, useCallback, useMemo, useEffect, ReactNode } from 'react';
import Axios from '../../../utils/axios';

// Reusable Modal Component
interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: ReactNode;
}

export const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    children
}) => {
    // Handle ESC key to close modal
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            // Allow body scroll for long modals
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 bg-black bg-opacity-50"
            onClick={onClose}
        >
            {/* Modal content with fullscreen dimensions */}
            <div
                className="bg-white shadow-xl flex flex-col h-screen w-screen"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Modal Header with Close button and Title */}
                <div
                    className="flex items-center justify-between px-6 py-4 flex-shrink-0"
                    style={{
                        borderBottom: '1px solid #EBEBEB'
                    }}
                >
                    {/* Left side - Close button */}
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        aria-label="Close modal"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24">
                            <path d="M12 10.586L16.95 5.636L18.364 7.05L13.414 12L18.364 16.95L16.95 18.364L12 13.414L7.05 18.364L5.636 16.95L10.586 12L5.636 7.05L7.05 5.636L12 10.586Z" fill="#5C5C5C" />
                        </svg>
                    </button>

                    {/* Center divider */}
                    <div
                        style={{
                            width: '0px',
                            height: '36px',
                            borderLeft: '1px solid #EBEBEB'
                        }}
                        className="mx-4"
                    ></div>

                    {/* Right side - Title */}
                    <div className="flex-1">
                        <h1
                            className="text-left"
                            style={{
                                fontWeight: 500,
                                fontSize: '16px',
                                lineHeight: '24px',
                                color: '#171717'
                            }}
                        >
                            {title}
                        </h1>
                    </div>
                </div>

                {/* Scrollable content area */}
                <div
                    className="flex-1 overflow-y-auto overflow-x-hidden"
                    style={{
                        padding: '32px'
                    }}
                >
                    {children}
                </div>
            </div>
        </div>
    );
};

// Reusable Table Modal Component
interface TableModalProps<T extends Record<string, any>> {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    modalTitle: string;
    description: string;
    data: T[];
    columns: Array<{
        key: string;
        title: string;
        sortable: boolean;
        render: (value: any) => React.ReactNode;
    }>;
    sortConfig: {
        defaultField: string;
        defaultDirection: 'asc' | 'desc';
    };
    csvFileName: string;
    onSort: (data: T[], field: string, direction: 'asc' | 'desc') => T[];
    getColumnOrder: () => string[];
    getCsvValue: (row: T, field: string) => string | number;
    loading?: boolean;
    hasMore?: boolean;
    onLoadMore?: () => void;
}

export function TableModal<T extends Record<string, any>>({
    isOpen,
    onClose,
    title,
    modalTitle,
    description,
    data,
    columns,
    sortConfig,
    csvFileName,
    onSort,
    getColumnOrder,
    getCsvValue,
    loading = false,
    hasMore = false,
    onLoadMore
}: TableModalProps<T>) {
    const [sortField, setSortField] = useState<string>(sortConfig.defaultField);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(sortConfig.defaultDirection);

    // Sort data based on current sort field and direction
    const sortedData = useMemo(() => {
        if (!sortField || !sortDirection) return data;

        const sorted = [...data].sort((a, b) => {
            const aValue = a[sortField];
            const bValue = b[sortField];

            // Handle null/undefined values
            if (aValue === null || aValue === undefined) {
                return sortDirection === 'asc' ? -1 : 1;
            }
            if (bValue === null || bValue === undefined) {
                return sortDirection === 'asc' ? 1 : -1;
            }

            if (aValue === bValue) return 0;

            // Handle different data types
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return sortDirection === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }

            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
            }

            // Try converting to number for numeric sorting
            const aNum = Number(aValue);
            const bNum = Number(bValue);

            if (!isNaN(aNum) && !isNaN(bNum)) {
                return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
            }

            // Fallback to string comparison
            const aStr = String(aValue);
            const bStr = String(bValue);
            return sortDirection === 'asc'
                ? aStr.localeCompare(bStr)
                : bStr.localeCompare(aStr);
        });

        return sorted;
    }, [data, sortField, sortDirection]);

    // Infinite scroll handler
    useEffect(() => {
        const handleScroll = (event: Event) => {
            if (!hasMore || loading || !onLoadMore) return;

            const target = event.target as HTMLElement;
            if (!target) return;

            // Check if user has scrolled near the bottom of modal content
            const scrollTop = target.scrollTop;
            const scrollHeight = target.scrollHeight;
            const clientHeight = target.clientHeight;

            if (scrollTop + clientHeight >= scrollHeight - 200) {
                onLoadMore();
            }
        };

        if (isOpen) {
            // Listen to scroll events on modal content area
            const modalContent = document.querySelector('.fixed.inset-0.z-50 .overflow-y-auto');
            if (modalContent) {
                modalContent.addEventListener('scroll', handleScroll);
                return () => modalContent.removeEventListener('scroll', handleScroll);
            }
        }
    }, [isOpen, hasMore, loading, onLoadMore]);

    // Auto-load more if content doesn't overflow yet (no scroll possible)
    useEffect(() => {
        if (!isOpen || !onLoadMore || loading || !hasMore) return;
        const modalContent = document.querySelector('.fixed.inset-0.z-50 .overflow-y-auto') as HTMLElement | null;
        if (!modalContent) return;

        // If scrollHeight <= clientHeight, there's nothing to scroll; ask for more data
        if (modalContent.scrollHeight <= modalContent.clientHeight) {
            onLoadMore();
        }
    }, [isOpen, sortedData.length, loading, hasMore, onLoadMore]);

    // Handle column sort (2-state toggle: asc <-> desc)
    const handleSort = useCallback((field: string, direction?: 'asc' | 'desc' | null) => {
        if (direction) {
            setSortDirection(direction);
            setSortField(field);
        } else {
            if (sortField === field) {
                // Simple 2-state toggle: asc <-> desc
                setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
            } else {
                // Different field clicked, start with asc for that field
                setSortField(field);
                setSortDirection('asc');
            }
        }
    }, [sortField, sortDirection]);

    // Download functionality - always use API, show error if API fails
    const handleDownload = useCallback(async () => {
        try {
            let apiEndpoint = '';
            
            // Determine the correct API endpoint based on CSV filename
            if (csvFileName === 'oem-distribution.csv') {
                apiEndpoint = '/v1/fleet/dashboard/oem-distribution/download/csv';
            } else if (csvFileName === 'city-distribution.csv') {
                apiEndpoint = '/v1/fleet/dashboard/city-distribution/download/csv';
            } else if (csvFileName === 'clients_by_active_ev.csv') {
                apiEndpoint = '/v1/fleet/dashboard/client-hub-distribution/download/csv';
            } else if (csvFileName === 'hub-location.csv') {
                apiEndpoint = '/v1/fleet/dashboard/hub-location/download/csv';
            } else {
                // For any other CSV types, show error
                console.error(`No API endpoint configured for CSV file: ${csvFileName}`);
                alert('CSV download failed: No API endpoint configured for this file type.');
                return;
            }

            // All CSV download APIs use GET
            const response = await Axios.get(apiEndpoint, {
                // If you need to send filters via query params in the future, 
                // you can add them to the 'params' object here
                // params: {
                //     status: ['active', 'idle'],
                // }
            });

            // Try multiple possible response formats
            let csvContent = null;
            let filename = csvFileName;

            // Check various possible response structures
            if (response.data?.data?.csvContent) {
                csvContent = response.data.data.csvContent;
                filename = response.data.data.filename || csvFileName;
            } else if (response.data?.csvContent) {
                csvContent = response.data.csvContent;
                filename = response.data.filename || csvFileName;
            } else if (response.data?.data && typeof response.data.data === 'string') {
                csvContent = response.data.data;
            } else if (typeof response.data === 'string' && response.data.includes(',')) {
                csvContent = response.data;
            }

            if (!csvContent || typeof csvContent !== 'string') {
                console.error('CSV download API response missing or invalid csvContent:', response.data);
                alert('CSV download failed: Invalid response from server. Please try again or contact support.');
                return;
            }

            // Create blob from CSV content string
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

            // Create download link
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

        } catch (error: any) {
            console.error('CSV download API error:', error);
            
            // Show user-friendly error message
            const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
            alert(`CSV download failed: ${errorMessage}. Please try again or contact support.`);
        }
    }, [csvFileName]);

    // Remove client-side CSV generation - no longer used

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title}>
            {/* Section Header */}
            <div
                className="flex items-center bg-white w-full"
                style={{
                    height: '56px',
                    marginBottom: '24px',
                    gap: '12px'
                }}
            >
                {/* Name section */}
                <div className="flex flex-col flex-1 gap-1">
                    {/* Title section */}
                    <div className="w-full">
                        <h2
                            className="flex-1"
                            style={{
                                fontWeight: 500,
                                fontSize: '24px',
                                lineHeight: '32px',
                                letterSpacing: '-0.006em',
                                color: '#171717'
                            }}
                        >
                            {modalTitle}
                        </h2>
                    </div>

                    {/* Description */}
                    <p
                        style={{
                            height: '20px',
                            fontWeight: 400,
                            fontSize: '14px',
                            lineHeight: '20px',
                            letterSpacing: '-0.006em',
                            color: '#5C5C5C'
                        }}
                    >
                        {description}
                    </p>
                </div>

                {/* Download Button */}
                <button
                    onClick={handleDownload}
                    className="flex items-center justify-center bg-white border hover:bg-gray-50 transition-colors"
                    style={{
                        width: '121px',
                        height: '40px',
                        padding: '10px 14px 10px 10px',
                        gap: '8px',
                        borderColor: '#EBEBEB',
                        borderWidth: '1px',
                        borderRadius: '10px',
                        boxShadow: '0px 1px 2px rgba(10, 13, 20, 0.03)'
                    }}
                >
                    <svg
                        style={{ width: '20px', height: '20px' }}
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <path d="M13 10H18L12 16L6 10H11V3H13V10ZM4 19H20V12H22V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20V12H4V19Z" fill="#5C5C5C" />
                    </svg>
                    <span
                        style={{
                            fontWeight: 500,
                            fontSize: '14px',
                            lineHeight: '20px',
                            letterSpacing: '-0.006em',
                            color: '#5C5C5C'
                        }}
                    >
                        Download
                    </span>
                </button>
            </div>

            {/* Table container - no height limit, let browser handle scrolling */}
            <div
                className="bg-white border rounded-lg w-full"
                style={{
                    borderColor: '#EBEBEB',
                    borderWidth: '1px',
                    borderRadius: '8px',
                    flex: 'none'
                }}
            >
                <div className="w-full">
                    <table className="w-full border-collapse bg-white">
                        <thead className="bg-gray-50">
                            <tr>
                                {columns.map((column) => (
                                    <th
                                        key={column.key}
                                        className="text-left font-normal text-sm text-neutral-600 px-3 py-2 border-0 bg-gray-50 whitespace-nowrap"
                                    >
                                        <div className="flex items-center gap-1">
                                            <span>{column.title}</span>
                                            {column.sortable && (
                                                <button
                                                    onClick={() => handleSort(column.key)}
                                                    className="text-gray-400 hover:text-gray-600"
                                                >
                                                    <i
                                                        className={`text-xs ${sortField === column.key
                                                            ? sortDirection === 'asc'
                                                                ? 'ri-arrow-up-line'
                                                                : sortDirection === 'desc'
                                                                    ? 'ri-arrow-down-line'
                                                                    : 'ri-expand-up-down-line'
                                                            : 'ri-expand-up-down-line'
                                                            }`}
                                                    ></i>
                                                </button>
                                            )}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {sortedData.map((row, index) => (
                                <tr key={index} className="h-11 border-b border-b-gray-200 last:border-0 hover:bg-gray-50">
                                    {columns.map((column, colIndex) => (
                                        <td
                                            key={column.key}
                                            className={`text-sm px-3 py-3 border-0 ${colIndex === 0
                                                ? 'font-medium text-neutral-900'
                                                : 'font-normal text-neutral-900'
                                                }`}
                                        >
                                            {column.render(row[column.key])}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                
                {/* Loading indicator for infinite scroll */}
                {loading && (
                    <div className="p-4 text-center">
                        <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                            <span className="ml-2 text-gray-600">Loading more...</span>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
}